import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, Stack, Tab, Tabs, Typography } from "@mui/material";
import { z } from "zod";

import { packNetAxios } from "@/lib/api-client";
import { DetailsContainer } from "@/components/details-container";
import { Loading } from "@/components/loading";
import { FailedRetry } from "@/components/failed-retry";
import { baseRoutes } from "@/api/base-routes";

import OptimalProductionBlocked, { type BlockedDetail } from "./tabs/optimal-production-blocked";
import OptimalProductionNonOptimal, { type NonOptimalDetail } from "./tabs/optimal-production-nonoptimal";
import OptimalProductionFailed, { type FailedJobDetails } from "./tabs/optimal-production-failed";

const SECTIONS = {
	blocked: "blocked",
	nonOptimal: "non-optimal",
	failed: "failed",
} as const;

const sectionSchema = z.enum(["blocked", "non-optimal", "failed"]).catch(SECTIONS.blocked);

type Section = z.infer<typeof sectionSchema>;

interface OptimalProductionData {
	details: {
		blocked: BlockedDetail[];
		failed: FailedJobDetails[];
		nonOptimal: NonOptimalDetail[];
	};
	counts: {
		blocked: number;
		failed: number;
		nonOptimal: number;
	};
}

const getOptimalProductionDetails = async (): Promise<OptimalProductionData> => {
	const response = await packNetAxios.get<OptimalProductionData>(
		`${baseRoutes.audit}/v1/CurrentState/optimalProduction/details`,
	);
	return response.data;
};

export const OptimalProductionDetails = () => {
	const [searchParams] = useSearchParams();
	const [activeTab, setActiveTab] = useState<Section>(() => sectionSchema.parseWithTelemetry(searchParams.get("tab")));

	const { data, isLoading, refetch, error } = useQuery({
		queryKey: ["Audit-LimitedProducibility"],
		queryFn: getOptimalProductionDetails,
		refetchInterval: 15000,
	});

	if (isLoading) {
		return <Loading />;
	}

	if (error) {
		return <FailedRetry retry={refetch} />;
	}

	return (
		<DetailsContainer fillContent>
			<Stack gap={2} sx={{ height: "100%" }}>
				<Typography variant="h2">
					<FormattedMessage id="Work to Produce" defaultMessage="Work to Produce" />
				</Typography>

				<Tabs value={activeTab} onChange={(_, value: Section) => setActiveTab(value)}>
					<Tab value={SECTIONS.blocked} label={<FormattedMessage id="Blocked" defaultMessage="Blocked" />} />
					<Tab value={SECTIONS.nonOptimal} label={<FormattedMessage id="Non-optimal" defaultMessage="Non-optimal" />} />
					<Tab value={SECTIONS.failed} label={<FormattedMessage id="Failed" defaultMessage="Failed" />} />
				</Tabs>

				<Card sx={{ height: "100%" }}>
					<CardContent sx={{ height: "100%" }}>
						{activeTab === SECTIONS.blocked && (
							<OptimalProductionBlocked details={data?.details.blocked ?? []} count={data?.counts.blocked ?? 0} />
						)}
						{activeTab === SECTIONS.nonOptimal && (
							<OptimalProductionNonOptimal
								details={data?.details.nonOptimal ?? []}
								count={data?.counts.nonOptimal ?? 0}
							/>
						)}
						{activeTab === SECTIONS.failed && (
							<OptimalProductionFailed
								details={data?.details.failed ?? []}
								count={data?.counts.failed ?? 0}
								refetch={refetch}
							/>
						)}
					</CardContent>
				</Card>
			</Stack>
		</DetailsContainer>
	);
};
